import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function findAnchorTarget(hash: string): HTMLElement | null {
    if (!hash) return null;
    try {
        return document.getElementById(decodeURIComponent(hash.slice(1)));
    } catch {
        return null;
    }
}

function isPageReload(): boolean {
    const [navigation] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    return navigation?.type === 'reload';
}

export default function ScrollToTop() {
    const location = useLocation();
    const navigationType = useNavigationType();
    const navigate = useNavigate();
    const previousPathname = useRef(location.pathname);
    const isFirstRender = useRef(true);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            if (isPageReload()) {
                window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
                if (location.hash) navigate({ pathname: location.pathname, search: location.search }, { replace: true, preventScrollReset: true });
                return;
            }
        }

        const pathChanged = previousPathname.current !== location.pathname;
        previousPathname.current = location.pathname;
        if (navigationType === 'REPLACE' && !pathChanged) return;
        const behavior: ScrollBehavior = window.matchMedia(REDUCED_MOTION_QUERY).matches ? 'auto' : 'smooth';
        const target = findAnchorTarget(location.hash);

        if (target) {
            target.scrollIntoView({ behavior: pathChanged ? 'auto' : behavior, block: 'start' });
            return;
        }

        if (pathChanged) window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, [location.key, location.pathname, location.search, location.hash, navigationType, navigate]);

    return null;
}
